import React, { useEffect, useState } from "react";
import {
  ProjectAPI,
  BlobAPI,
  SessionAPI,
  AuthAPI,
} from "../services/api";

import { useNavigate } from "react-router-dom";
import { useSession } from "../context/SessionContext";
import { SessionAPI as ContractsSessionAPI } from "../Contracts/Services/Api";
import { useSession as useContractsSession } from "../Contracts/Contexts/SessionContext";

import { Project } from "../types/project";
import { UserCompany } from "../services/api";

const Projects = () => {
  // ============================================================
  // PROJECT STATE
  // ============================================================

  const [projects, setProjects] = useState<Project[]>([]);

  // ============================================================
  // USER COMPANY MEMBERSHIPS
  // ============================================================

  const [userCompanies, setUserCompanies] =
    useState<UserCompany[]>([]);

  // ============================================================
  // UI STATE
  // ============================================================

  const [showModal, setShowModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCreateCompanyNo, setSelectedCreateCompanyNo] =
    useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [activeTab, setActiveTab] =
    useState("projects");

  // ============================================================
  // USER DETAILS
  // ============================================================

  const userName =
    localStorage.getItem("userName");

  const userNo =
    localStorage.getItem("userNo");

  // ============================================================
  // NAVIGATION / SESSION
  // ============================================================

  const navigate = useNavigate();

  // MBIM session: used by WBS and MBIM screens.
  const { setSessionNo } = useSession();

  // Contracts session: kept separate from the MBIM session.
  const { setSession: setContractsSession } = useContractsSession();

  // Clear MBIM session storage while preserving the Contracts session keys.
  // These names must match Contracts/Contexts/SessionContext.tsx.
  const clearMbimSessionStorage = () => {
    const contractsSessionNo = sessionStorage.getItem("contractsSessionNo");
    const contractsProjectNo = sessionStorage.getItem("contractsProjectNo");

    sessionStorage.clear();

    if (contractsSessionNo !== null) {
      sessionStorage.setItem("contractsSessionNo", contractsSessionNo);
    }

    if (contractsProjectNo !== null) {
      sessionStorage.setItem("contractsProjectNo", contractsProjectNo);
    }
  };

  // ============================================================
  // CREATE PROJECT FORM
  // ============================================================

  const [form, setForm] = useState({
    projectName: "",
    projectDescription: "",
    startDate: "",
    image: null as File | null,
  });

  // ============================================================
  // COLORS
  // ============================================================

  const colors = [
    "from-slate-400 to-slate-600",
    "from-gray-400 to-gray-600",
    "from-zinc-400 to-zinc-600",
    "from-neutral-400 to-neutral-600",
  ];

  // ============================================================
  // STABLE COLOR GENERATOR
  // ============================================================

  const getColor = (name: string) => {
    const index =
      name
        .split("")
        .reduce(
          (acc, char) =>
            acc + char.charCodeAt(0),
          0
        ) % colors.length;

    return colors[index];
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    loadUserCompanies();
    loadProjects();
  }, []);

  // ============================================================
  // LOAD USER COMPANIES
  //
  // UserType is taken from UserCompany through /Auth/me
  // ============================================================

  const loadUserCompanies = async () => {
    try {
      const response =
        await AuthAPI.me();

      const companies =
        response.data?.companies || [];

      setUserCompanies(companies);

    } catch (err) {
      console.error(
        "Failed to load user companies:",
        err
      );
    }
  };

  // ============================================================
  // LOAD PROJECTS
  // ============================================================

  const loadProjects = async () => {
    try {
      const data =
        await ProjectAPI.getAll();

      const projectsWithImages =
        await Promise.all(
          data.map(
            async (p: Project) => {
              try {
                const imageUrl =
                  await BlobAPI.getImage(
                    "Project",
                    p.projectNo
                  );

                return {
                  ...p,
                  imageUrl,
                };
              } catch {
                return {
                  ...p,
                  imageUrl: null,
                };
              }
            }
          )
        );

      setProjects(
        projectsWithImages as Project[]
      );

    } catch (err) {
      console.error(err);
    }
  };

  // ============================================================
  // GET USER ROLE FOR COMPANY
  //
  // IMPORTANT:
  // Role is NOT global.
  // It is taken from UserCompany for this company.
  // ============================================================

  const getCompanyUserType = (
    companyNo: number
  ): string | null => {
    const membership =
      userCompanies.find(
        (uc) =>
          uc.companyNo === companyNo
      );

    return (
      membership?.userType
        ?.toLowerCase() || null
    );
  };

  // ============================================================
  // CHECK ADMIN ROLE FOR COMPANY
  // ============================================================

  const isCompanyAdmin = (
    companyNo: number
  ): boolean => {
    const userType =
      getCompanyUserType(companyNo);

    return (
      userType === "admin" ||
      userType === "superadmin"
    );
  };

  // ============================================================
  // CHECK SUPERADMIN ROLE FOR COMPANY
  // ============================================================

  const isCompanySuperAdmin = (
    companyNo: number
  ): boolean => {
    const userType =
      getCompanyUserType(companyNo);

    return (
      userType === "superadmin"
    );
  };

  // ============================================================
  // TRIAL PROJECT
  // ============================================================

  const openTrialProject = async () => {
    try {
      const trialProjectNo = 67;

      clearMbimSessionStorage();

      const session =
        await SessionAPI.create(
          trialProjectNo,
          Number(userNo)
        );

      setSessionNo(
        session.sessionNo
      );

      navigate(
        `/project/${trialProjectNo}`,
        {
          state: {
            projectName:
              "Trial Project",
          },
        }
      );

    } catch (err) {
      console.error(err);

      alert(
        "Unable to open Trial Project"
      );
    }
  };

  // ============================================================
  // SAMPLE PROJECT
  // ============================================================

  const openSampleProject = async () => {
    try {
      const sampleProjectNo = 24;

      clearMbimSessionStorage();

      const session =
        await SessionAPI.create(
          sampleProjectNo,
          Number(userNo)
        );

      setSessionNo(
        session.sessionNo
      );

      // Open only Sample Project NLBD screen
      navigate(
        `/project/${sampleProjectNo}/third`,
        {
          state: {
            projectName:
              "Sample Project",
          },
        }
      );

    } catch (err) {
      console.error(err);

      alert(
        "Unable to open Sample Project"
      );
    }
  };

  // ============================================================
  // SEARCH PROJECTS
  // ============================================================

  const filteredProjects =
    projects.filter((p) =>
      p.projectName
        .toLowerCase()
        .includes(
          search.toLowerCase()
        )
    );

  // ============================================================
  // GROUP PROJECTS BY COMPANY
  // ============================================================

  const groupedProjects =
    filteredProjects.reduce(
      (
        groups,
        project
      ) => {
        const companyNo =
          project.companyNo;

        if (!groups[companyNo]) {
          groups[companyNo] = {
            companyName:
              project.companyName ||
              "Company",
            projects: [],
          };
        }

        groups[
          companyNo
        ].projects.push(
          project
        );

        return groups;
      },
      {} as Record<
        number,
        {
          companyName: string;
          projects: Project[];
        }
      >
    );

  // ============================================================
  // CREATE PROJECT
  // ============================================================

  const openCreateProject = () => {
    setSelectedCreateCompanyNo(null);
    setShowCompanyModal(true);
  };

  const handleCreate = async () => {
    if (
      !form.projectName ||
      !form.projectDescription ||
      !form.startDate
    ) {
      alert(
        "All fields are required"
      );

      return;
    }

    if (!selectedCreateCompanyNo) {
      alert("Please select a company");
      return;
    }

    try {
      setLoading(true);

      // --------------------------------------------------------
      // CREATE PROJECT
      // --------------------------------------------------------

      const formData =
        new FormData();

      formData.append(
        "projectName",
        form.projectName
      );

      formData.append(
        "projectDescription",
        form.projectDescription
      );

      formData.append(
        "startDate",
        form.startDate
      );

      // Send the selected company to the backend.
      formData.append(
        "companyNo",
        selectedCreateCompanyNo.toString()
      );

      const data =
        await ProjectAPI.create(
          formData
        );

      const projectNo =
        Number(data?.projectNo);

      if (
        !Number.isFinite(
          projectNo
        )
      ) {
        throw new Error(
          "Project number was not returned from API"
        );
      }

      // --------------------------------------------------------
      // UPLOAD IMAGE
      // --------------------------------------------------------

      if (form.image) {
        await BlobAPI.uploadFileOnly(
          form.image,
          {
            containerName:
              "project-images",

            entityType:
              "Project",

            entityNo:
              projectNo,

            project:
              projectNo,

            sno: 1,
          }
        );
      }

      alert(
        "Project and image uploaded successfully"
      );

      setShowModal(false);
      setSelectedCreateCompanyNo(null);

      setForm({
        projectName: "",
        projectDescription: "",
        startDate: "",
        image: null,
      });

      await loadProjects();

    } catch (err: any) {
      console.error(
        "CREATE ERROR:",
        err
      );

      const msg =
        typeof err?.response
          ?.data === "string"
          ? err.response.data
          : err?.response?.data
              ?.message ||
            err?.message ||
            "Request failed";

      alert(msg);

    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // OPEN PROJECT
  // ============================================================

  const openProject = async (
    project: Project
  ) => {
    try {
      clearMbimSessionStorage();

      const session =
        await SessionAPI.create(
          project.projectNo,
          Number(userNo)
        );

      setSessionNo(
        session.sessionNo
      );

      setTimeout(() => {
        navigate(
          `/project/${project.projectNo}`,
          {
            state: {
              projectName:
                project.projectName,
            },
          }
        );
      }, 50);

    } catch (err) {
      console.error(err);

      alert(
        "Session creation failed"
      );
    }
  };

  // ============================================================
  // OPEN CONTRACTS FOR THE SELECTED PROJECT
  // ============================================================

  const openContracts = async (project: Project) => {
    try {
      if (!userNo || !Number.isFinite(Number(userNo))) {
        alert("User information not found. Please log in again.");
        return;
      }

      // Create a session through the Contracts API, not the MBIM API.
      const session = await ContractsSessionAPI.create(
        project.projectNo,
        Number(userNo)
      );

      // Update only the Contracts session context.
      setContractsSession(session.sessionNo, project.projectNo);

      // Keep the selected project available to existing Contracts pages.
      localStorage.setItem("projectNo", String(project.projectNo));
      localStorage.setItem("projectName", project.projectName);

      navigate(`/projects/${project.projectNo}/contracts`, {
        state: {
          projectName: project.projectName,
        },
      });
    } catch (err) {
      console.error("Failed to open Contracts:", err);
      alert("Unable to open Contracts for this project");
    }
  };

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    setActiveTab("logout");

    localStorage.clear();

    setSessionNo(null);

    navigate("/login");
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 p-6"
      style={{
        fontFamily:
          "'Segoe UI', sans-serif",
        fontSize: "13px",
        fontWeight: 400,
      }}
    >

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="flex justify-between items-center mb-4">

        <div>

          <h1
            className="text-3xl font-bold text-gray-800"
            style={{
              fontFamily:
                "'Segoe UI', sans-serif",
            }}
          >
            Welcome,{" "}
            {userName || "User"}
          </h1>

          <p className="text-gray-500">
            {projects.length} Projects
          </p>

        </div>

        {/* ====================================================
            HEADER BUTTONS
        ==================================================== */}

        <div className="flex gap-3">

          {/* REGISTER NEW COMPANY */}

          <button
            type="button"
            onClick={() => {
              setActiveTab(
                "registercompany"
              );

              navigate(
                "/register-new-company"
              );
            }}
            className={`px-4 py-2 border-b-2 ${
              activeTab ===
              "registercompany"
                ? "border-[#5F5FEA] text-[#5F5FEA] font-semibold"
                : "border-transparent text-gray-700 hover:border-[#5F5FEA] hover:text-[#5F5FEA]"
            }`}
          >
            Register New Company
          </button>

          {/* TRIAL PROJECT */}

          <button
            onClick={
              openTrialProject
            }
            className="px-4 py-2 border-b-2 border-transparent text-gray-700 hover:border-[#5F5FEA] hover:text-[#5F5FEA]"
          >
            Trial Project
          </button>

          {/* SAMPLE PROJECT */}

          <button
            type="button"
            onClick={
              openSampleProject
            }
            className="px-4 py-2 border-b-2 border-transparent text-gray-700 hover:border-[#5F5FEA] hover:text-[#5F5FEA]"
          >
            Sample Project
          </button>

          {/* LOGOUT */}

          <button
            onClick={
              handleLogout
            }
            className={`px-4 py-2 border-b-2 ${
              activeTab === "logout"
                ? "border-[#5F5FEA] text-gray-700 font-semibold"
                : "border-transparent text-gray-700 hover:border-[#5F5FEA] hover:text-[#5F5FEA]"
            }`}
          >
            Logout
          </button>

        </div>
      </div>

      {/* ======================================================
          SEARCH
      ====================================================== */}

      <input
        type="text"
        placeholder="🔍 Search projects..."
        className="mb-6 w-full max-w-sm px-4 py-2 border rounded-xl shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
        value={search}
        onChange={(e) =>
          setSearch(
            e.target.value
          )
        }
      />

      {/* ======================================================
          COMPANY-WISE PROJECTS
      ====================================================== */}

      <div className="space-y-8">

        {Object.entries(
          groupedProjects
        ).map(
          ([
            companyNo,
            company,
          ]) => {

            const companyNumber =
              Number(
                companyNo
              );

            const userType =
              getCompanyUserType(
                companyNumber
              );

            const isAdmin =
              isCompanyAdmin(
                companyNumber
              );

            const isSuperAdmin =
              isCompanySuperAdmin(
                companyNumber
              );

            return (
              <div
                key={companyNo}
              >

                {/* ==========================================
                    COMPANY HEADER
                ========================================== */}

                <div className="mb-4">

                  <div className="flex items-center justify-between">

                    <div>

                      <h2 className="text-xl font-semibold text-gray-800">
                        {
                          company.companyName
                        }
                      </h2>

                      <p className="text-xs text-gray-500 mt-1">
                        {userType
                          ? userType
                          : "Role not assigned"}
                      </p>

                    </div>

                    {/* ======================================
                        COMPANY ADMIN CONTROLS
                    ====================================== */}

                    <div className="flex gap-3">
                    {isSuperAdmin && (
  <button
    type="button"
    onClick={() => {
      setActiveTab("superadmin");
      navigate("/super-admin");
    }}
    className="px-4 py-2 border-b-2 border-transparent text-gray-700 hover:border-[#5F5FEA] hover:text-[#5F5FEA]"
  >
    Super Admin
  </button>
)}
                      {isAdmin && (
                        <button
                          onClick={() => {
                            setActiveTab(
                              "createproject"
                            );

                            openCreateProject();
                          }}
                          className="px-4 py-2 border-b-2 border-transparent text-gray-700 hover:border-[#5F5FEA] hover:text-[#5F5FEA]"
                        >
                          Create Project
                        </button>
                      )}

                    </div>

                  </div>

                  <div className="mt-2 border-b border-gray-300" />

                </div>

                {/* ==========================================
                    PROJECT GRID
                ========================================== */}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">

                  {company.projects.map(
                    (p: Project & {
                      imageUrl?: string | null;
                    }) => {

                      const color =
                        getColor(
                          p.projectName
                        );

                      return (
                        <div
                          key={
                            p.projectNo
                          }
                          onClick={() =>
                            openProject(
                              p
                            )
                          }
                          className="
                            relative
                            bg-white
                            rounded-2xl
                            shadow-md
                            hover:shadow-xl
                            transition-all
                            duration-200
                            cursor-pointer
                            overflow-hidden
                            group
                            hover:-translate-y-1
                            border
                            border-gray-200
                            hover:border-blue-400
                          "
                        >

                          {/* =================================
                              IMAGE
                          ================================= */}

                          {p.imageUrl ? (

                            <div className="relative overflow-hidden">

                              <img
                                src={
                                  p.imageUrl
                                }
                                className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
                                alt={
                                  p.projectName
                                }
                              />

                              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition" />

                            </div>

                          ) : (

                            <div
                              className={`w-full aspect-video bg-gradient-to-br ${color} flex flex-col justify-end p-4`}
                            >

                              <h2 className="text-white truncate">
                                {
                                  p.projectName
                                }
                              </h2>

                              <p className="text-white/70">
                                Project
                              </p>

                            </div>
                          )}

                          {/* =================================
                              PROJECT DETAILS
                          ================================= */}

                          <div className="p-3 bg-gray-50 space-y-1">

                            <div className="flex min-w-0 items-center gap-2">
                              <h2 className="min-w-0 flex-1 truncate text-gray-800 group-hover:text-blue-600 transition">
                                {p.projectName}
                              </h2>

                              {/* Compact actions appear at the right end of the project-name row on hover */}
                              <div
                                className="flex shrink-0 gap-1 opacity-0 pointer-events-none transition-opacity duration-200 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                                aria-label={`Actions for ${p.projectName}`}
                              >
                                <button
                                  type="button"
                                  title="Open WBS"
                                  aria-label={`Open WBS for ${p.projectName}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void openProject(p);
                                  }}
                                  className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-400 bg-white text-gray-800 transition hover:bg-gray-100 hover:text-black focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                                    <rect x="3" y="3" width="7" height="7" rx="1" />
                                    <rect x="14" y="3" width="7" height="7" rx="1" />
                                    <rect x="14" y="14" width="7" height="7" rx="1" />
                                    <path d="M6.5 14H10v7H3v-3.5" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  title="Open Contracts"
                                  aria-label={`Open Contracts for ${p.projectName}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void openContracts(p);
                                  }}
                                  className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-400 bg-white text-gray-800 transition hover:bg-gray-100 hover:text-black focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <path d="M14 2v6h6" />
                                    <path d="M8 13h8M8 17h8M8 9h2" />
                                  </svg>
                                </button>
                              </div>
                            </div>

                            {p.startDate && (
                              <p className="text-gray-500">
                                {new Date(
                                  p.startDate
                                ).toLocaleDateString()}
                              </p>
                            )}


                          </div>

                        </div>
                      );
                    }
                  )}

                </div>

              </div>
            );
          }
        )}

      </div>

      {/* ======================================================
          NO PROJECTS
      ====================================================== */}

      {filteredProjects.length ===
        0 && (
          <div className="text-center text-gray-400 mt-10">
            No projects found
          </div>
        )}

      {/* ======================================================
          SELECT COMPANY FOR CREATE PROJECT
      ====================================================== */}

      {showCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-2xl">

            <h2 className="text-xl font-semibold mb-2">
              Select Company
            </h2>

            <p className="text-gray-500 mb-4">
              Select the company where you want to create the project.
            </p>

            <div className="space-y-2">
              {userCompanies
                .filter(
                  (company) =>
                    company.userType?.toLowerCase() === "admin" ||
                    company.userType?.toLowerCase() === "superadmin"
                )
                .map((company) => (
                  <button
                    key={company.companyNo}
                    type="button"
                    onClick={() => {
                      setSelectedCreateCompanyNo(company.companyNo);
                      setShowCompanyModal(false);
                      setShowModal(true);
                    }}
                    className="w-full text-left border p-3 rounded-lg hover:bg-blue-50"
                  >
                    <div className="font-semibold">
                      {company.companyName}
                    </div>
                    <div className="text-xs text-gray-500">
                      Company No: {company.companyNo}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      Role: {company.userType}
                    </div>
                  </button>
                ))}
            </div>

            <button
              type="button"
              onClick={() => setShowCompanyModal(false)}
              className="mt-4 bg-gray-300 px-4 py-2 rounded-lg"
            >
              Cancel
            </button>

          </div>
        </div>
      )}

      {/* ======================================================
          CREATE PROJECT MODAL
      ====================================================== */}

      {showModal && (

        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">

          <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-2xl">

            <h2 className="mb-4">
              Create Project Details
            </h2>

            {selectedCreateCompanyNo && (
              <div className="mb-4 bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <div className="text-xs text-gray-500">
                  Selected Company
                </div>
                <div className="font-semibold text-blue-700">
                  {userCompanies.find(
                    (company) =>
                      company.companyNo === selectedCreateCompanyNo
                  )?.companyName}
                </div>
              </div>
            )}

            {/* PROJECT NAME */}

            <label className="block mb-1 font-medium text-gray-700">
              Project Name
            </label>

            <input
              type="text"
              placeholder="Enter Project Name"
              className="w-full border p-2 mb-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
              value={
                form.projectName
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  projectName:
                    e.target.value,
                })
              }
            />

            {/* DESCRIPTION */}

            <label className="block mb-1 font-medium text-gray-700">
              Project Description
            </label>

            <input
              type="text"
              placeholder="Enter Project Description"
              className="w-full border p-2 mb-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
              value={
                form.projectDescription
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  projectDescription:
                    e.target.value,
                })
              }
            />

            {/* START DATE */}

            <label className="block mb-1 font-medium text-gray-700">
              Start Date
            </label>

            <input
              type="date"
              className="w-full border p-2 mb-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
              value={
                form.startDate
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  startDate:
                    e.target.value,
                })
              }
            />

            {/* PROJECT IMAGE */}

            <label className="block mb-1 font-medium text-gray-700">
              Project Image
            </label>

            <input
              type="file"
              accept="image/*"
              className="w-full border p-2 mb-3 rounded-lg bg-white"
              onChange={(e) =>
                setForm({
                  ...form,
                  image:
                    e.target.files?.[0] ||
                    null,
                })
              }
            />

            {/* IMAGE PREVIEW */}

            {form.image && (
              <img
                src={URL.createObjectURL(
                  form.image
                )}
                className="w-20 h-20 rounded-full object-cover mb-3"
                alt="Project preview"
              />
            )}

            {/* BUTTONS */}

            <div className="flex justify-end gap-2">

              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedCreateCompanyNo(null);
                }}
                className="bg-gray-300 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={
                  handleCreate
                }
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg"
              >
                {loading
                  ? "Creating..."
                  : "Create"}
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

export default Projects;  